import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Trash2, GripVertical, Route, BookOpen, X, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableModule({ mod, index, onRemove, isAdmin }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: mod.id, disabled: !isAdmin });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.7 : 1, zIndex: isDragging ? 10 : 0 };
  return (
    <div ref={setNodeRef} style={style} className={`flex items-center gap-3 p-3 rounded-xl border bg-white ${isDragging ? 'shadow-lg border-[#1B2A4A]' : 'border-stone-200'}`} data-testid={`path-builder-module-${mod.id}`}>
      {isAdmin && <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-stone-400 hover:text-stone-600" data-testid={`path-drag-${mod.id}`}><GripVertical className="w-4 h-4" /></button>}
      <div className="w-7 h-7 rounded-full bg-[#1B2A4A] text-white text-xs font-bold flex items-center justify-center shrink-0">{index + 1}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-stone-900 truncate">{mod.title}</p>
        <p className="text-[11px] text-stone-400">{mod.lesson_count} {mod.module_type === 'modular' ? 'sections' : 'lessons'} · {mod.module_type === 'modular' ? 'Modular (section quizzes)' : 'Document + Quiz'}</p>
      </div>
      {isAdmin && <Button variant="ghost" size="sm" onClick={() => onRemove(mod.id)} className="text-stone-400 hover:text-red-600 h-7 w-7 p-0" data-testid={`path-remove-module-${mod.id}`}><X className="w-4 h-4" /></Button>}
    </div>
  );
}

function PathCard({ path, allModules, isAdmin, onChanged }) {
  const { api } = useAuth();
  const [open, setOpen] = useState(path.path_type === 'general');
  const [showAdd, setShowAdd] = useState(false);
  const [addModuleId, setAddModuleId] = useState('');
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const inPath = new Set(path.modules.map(m => m.id));
  const available = allModules.filter(m => !inPath.has(m.id));

  const saveOrder = async (ids) => {
    try { await api('put', `/learning-paths/${path.id}`, { module_ids: ids }); onChanged(); }
    catch { toast.error('Failed to reorder'); }
  };
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = path.modules.map(m => m.id);
    saveOrder(arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id)));
  };
  const addModule = async () => {
    if (!addModuleId) return;
    try { await api('post', `/learning-paths/${path.id}/modules`, { module_id: addModuleId }); toast.success('Module added'); setShowAdd(false); setAddModuleId(''); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to add module'); }
  };
  const removeModule = async (mid) => {
    try { await api('delete', `/learning-paths/${path.id}/modules/${mid}`); onChanged(); }
    catch { toast.error('Failed to remove module'); }
  };
  const deletePath = async () => {
    if (!window.confirm(`Delete "${path.name}"? Sign-off records for this path will be removed.`)) return;
    try { await api('delete', `/learning-paths/${path.id}`); toast.success('Path deleted'); onChanged(); }
    catch { toast.error('Failed to delete path'); }
  };

  return (
    <Card className="bg-white rounded-2xl border border-stone-200 shadow-sm" data-testid={`path-card-${path.id}`}>
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3 cursor-pointer" onClick={() => setOpen(!open)}>
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${path.path_type === 'general' ? 'bg-[#D9480F]/10 text-[#D9480F]' : 'bg-[#1B2A4A]/10 text-[#1B2A4A]'}`}><Route className="w-5 h-5" /></div>
            <div className="min-w-0">
              <h3 className="font-semibold text-stone-900 truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>{path.name}</h3>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                {path.path_type === 'general' ? <Badge className="bg-[#D9480F]/10 text-[#D9480F] text-[10px]">Everyone first</Badge> : (
                  <><Badge className={path.department === 'FOH' ? 'bg-blue-100 text-blue-700 text-[10px]' : 'bg-orange-100 text-orange-700 text-[10px]'}>{path.department}</Badge><Badge className="bg-violet-100 text-violet-700 text-[10px]">{path.position}</Badge></>
                )}
                <span className="text-xs text-stone-400">{path.modules.length} module{path.modules.length !== 1 ? 's' : ''}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {isAdmin && <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deletePath(); }} className="text-red-400 hover:text-red-600" data-testid={`delete-path-${path.id}`}><Trash2 className="w-4 h-4" /></Button>}
            {open ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
          </div>
        </div>
        {open && (
          <div className="mt-4">
            {path.modules.length === 0 ? (
              <p className="text-sm text-stone-400 text-center py-4 border border-dashed border-stone-200 rounded-xl">No modules yet — add the first module below</p>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={path.modules.map(m => m.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2">{path.modules.map((m, i) => <SortableModule key={m.id} mod={m} index={i} onRemove={removeModule} isAdmin={isAdmin} />)}</div>
                </SortableContext>
              </DndContext>
            )}
            {isAdmin && (
              <Button variant="outline" onClick={() => setShowAdd(true)} className="w-full mt-3 rounded-xl border-dashed border-stone-300 text-stone-600" data-testid={`add-module-to-path-${path.id}`}>
                <Plus className="w-4 h-4 mr-1" /> Add Module
              </Button>
            )}
          </div>
        )}
      </CardContent>
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="rounded-2xl" data-testid="add-module-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>Add Module to {path.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Training Module (document + quiz)</Label>
              <Select value={addModuleId} onValueChange={setAddModuleId}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="add-module-select"><SelectValue placeholder="Choose a module" /></SelectTrigger>
                <SelectContent>{available.map(m => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}</SelectContent>
              </Select>
              {available.length === 0 && <p className="text-xs text-stone-400 mt-2">All modules are already in this path. Upload a new document to create more.</p>}
            </div>
            <Button onClick={addModule} disabled={!addModuleId} className="w-full bg-[#1B2A4A] text-white rounded-xl hover:bg-[#243658]" data-testid="confirm-add-module-btn">Add to End of Path</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function PathsTab({ modules, isAdmin }) {
  const { api } = useAuth();
  const [paths, setPaths] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ path_type: 'position', name: '', department: 'FOH', position: '', description: '' });
  const [positions, setPositions] = useState([]);

  const load = useCallback(async () => {
    try { const { data } = await api('get', '/learning-paths'); setPaths(data); } catch { /* ignore */ }
  }, [api]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (form.path_type === 'position') api('get', `/positions?department=${form.department}`).then(r => setPositions(r.data)).catch(() => setPositions([]));
  }, [form.department, form.path_type, api]);

  const hasGeneral = paths.some(p => p.path_type === 'general');
  const createPath = async () => {
    try {
      await api('post', '/learning-paths', form);
      toast.success('Learning path created');
      setShowCreate(false); setForm({ path_type: 'position', name: '', department: 'FOH', position: '', description: '' }); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create path'); }
  };

  return (
    <div data-testid="paths-tab">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Learning Paths</h2>
          <p className="text-xs text-stone-500">Everyone completes General Onboarding first, then their position path. Each module needs manager sign-off before the next unlocks.</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowCreate(true)} className="bg-[#1B2A4A] text-white rounded-xl hover:bg-[#243658] text-sm" data-testid="create-path-btn"><Plus className="w-4 h-4 mr-1" /> New Path</Button>
        )}
      </div>
      {paths.length === 0 ? (
        <Card className="bg-white rounded-2xl border border-stone-200"><CardContent className="p-8 text-center"><BookOpen className="w-12 h-12 text-stone-300 mx-auto mb-3" /><p className="text-stone-500">No learning paths yet. Create the General Onboarding path to get started.</p></CardContent></Card>
      ) : (
        <div className="space-y-3" data-testid="paths-list">{paths.map(p => <PathCard key={p.id} path={p} allModules={modules} isAdmin={isAdmin} onChanged={load} />)}</div>
      )}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="rounded-2xl" data-testid="create-path-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>New Learning Path</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Path Type</Label>
              <Select value={form.path_type} onValueChange={v => setForm({ ...form, path_type: v })}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="path-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general" disabled={hasGeneral}>General Onboarding (everyone){hasGeneral ? ' — exists' : ''}</SelectItem>
                  <SelectItem value="position">Position Path</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.path_type === 'position' && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Department</Label>
                  <Select value={form.department} onValueChange={v => setForm({ ...form, department: v, position: '' })}>
                    <SelectTrigger className="rounded-xl mt-1" data-testid="path-department-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="FOH">FOH</SelectItem><SelectItem value="BOH">BOH</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Position</Label>
                  <Select value={form.position} onValueChange={v => setForm({ ...form, position: v })}>
                    <SelectTrigger className="rounded-xl mt-1" data-testid="path-position-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{positions.map(p => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <div><Label>Name <span className="text-stone-400 font-normal">(optional)</span></Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1" placeholder={form.path_type === 'general' ? 'General Onboarding' : `${form.position || 'Position'} Path`} data-testid="path-name-input" /></div>
            <div><Label>Description <span className="text-stone-400 font-normal">(optional)</span></Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-xl mt-1" placeholder="What this path covers" data-testid="path-description-input" /></div>
            <Button onClick={createPath} disabled={form.path_type === 'position' && !form.position} className="w-full bg-[#1B2A4A] text-white rounded-xl hover:bg-[#243658]" data-testid="confirm-create-path-btn">Create Path</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
